/* eslint-disable no-console -- operational logging for DB sync diagnostics */
/**
 * Local Database Initialization
 *
 * Handles database initialization and seeding of default data.
 */

import {
  localStore,
  isElectron,
  getElectronWindow,
  loadFromLocalStorage,
  saveToLocalStorage,
  getNormalBalance,
} from './store'
import { DEFAULT_CHART_OF_ACCOUNTS } from '../../constants/chart-of-accounts'
import { DEFAULT_PRIMARY_LEDGER_ID } from '../../constants/storage'
import { getOrganization } from '../organization-service'

import type { LocalLedger, LocalAccount, LocalJournalEntry } from './types'

interface ApiAccount {
  id: string; orgId: string; code: string; name: string
  description?: string; accountType: string; accountSubtype?: string
  normalBalance: string; parentId?: string; path?: string
  depth?: number; isHeader?: boolean; isSystem?: boolean; isActive: boolean
  audit?: { createdAt?: string; updatedAt?: string }
}

function toLocalAccount(acc: ApiAccount, ledgerId: string): LocalAccount {
  const nowIso = new Date().toISOString()
  return {
    id: acc.id,
    orgId: acc.orgId,
    ledgerId: ledgerId,
    code: acc.code,
    name: acc.name,
    description: acc.description ?? '',
    accountType: acc.accountType as import('../../constants/labels').AccountType,
    accountSubtype: acc.accountSubtype ?? '',
    normalBalance: acc.normalBalance as 'NORMAL_BALANCE_DEBIT' | 'NORMAL_BALANCE_CREDIT',
    parentId: acc.parentId,
    path: acc.path ?? '',
    depth: acc.depth ?? 0,
    isHeader: acc.isHeader ?? false,
    isSystem: acc.isSystem ?? false,
    isActive: acc.isActive,
    createdAt: acc.audit?.createdAt ?? nowIso,
    updatedAt: acc.audit?.updatedAt ?? nowIso,
  }
}

/**
 * Sync accounts from the server into the local store.
 * Extracted to reduce nesting depth in initializeLocalDb.
 */
async function syncAccountsFromServer(orgId: string, ledgerId: string): Promise<void> {
  /* v8 ignore next */
  const apiUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
  const accountsResponse = await fetch(
    `${apiUrl}/organizations/${orgId}/accounts?pagination.pageSize=1000&includeHeaders=true&includeInactive=true`
  )
  if (!accountsResponse.ok) { return }

  const accountsData = await accountsResponse.json() as { accounts?: ApiAccount[] }
  /* v8 ignore next */
  if (!accountsData.accounts) { return }

  for (const acc of accountsData.accounts) {
    localStore.accounts.set(acc.id, toLocalAccount(acc, ledgerId))
  }
}

/**
 * Sync journal entries from the server into the local store.
 * Extracted to reduce nesting depth in initializeLocalDb.
 */
async function syncJournalEntriesFromServer(orgId: string, ledgerId: string): Promise<void> {
  /* v8 ignore next */
  const apiUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
  const entriesResponse = await fetch(
    `${apiUrl}/organizations/${orgId}/journal-entries?page_size=1000`
  )
  if (!entriesResponse.ok) { return }

  interface ApiLine {
    id: string; lineNumber: number; accountId: string; isDebit: boolean; memo?: string
    amount?: { original?: { amount?: string; currency?: string } }
  }
  interface ApiEntry {
    id: string; orgId: string; entryNumber: string; entryDate: string
    postDate?: string; entryType?: string; memo?: string; reference?: string
    status: string; lines?: ApiLine[]
    audit?: { createdAt?: string; updatedAt?: string }
  }
  const entriesData = await entriesResponse.json() as { entries?: ApiEntry[] }
  /* v8 ignore next */
  if (!entriesData.entries) { return }

  for (const entry of entriesData.entries) {
    localStore.journalEntries.set(entry.id, buildLocalJournalEntry(entry, ledgerId))
  }
  console.log(`[LocalDB] Synced ${entriesData.entries.length} journal entries`)
}

interface ApiEntry {
  id: string; orgId: string; entryNumber: string; entryDate: string
  postDate?: string; entryType?: string; memo?: string; reference?: string
  status: string
  lines?: Array<{
    id: string; lineNumber: number; accountId: string; isDebit: boolean; memo?: string
    amount?: { original?: { amount?: string; currency?: string } }
  }>
  audit?: { createdAt?: string; updatedAt?: string }
}

function buildLocalJournalEntry(entry: ApiEntry, ledgerId: string): LocalJournalEntry {
  let status: 'draft' | 'posted' | 'voided' = 'draft'
  if (entry.status === 'ENTRY_STATUS_POSTED') { status = 'posted' }
  else if (entry.status === 'ENTRY_STATUS_VOIDED') { status = 'voided' }
  else if (entry.status === 'ENTRY_STATUS_DRAFT') { status = 'draft' }

  const entryType = entry.entryType?.replace('ENTRY_TYPE_', '').toLowerCase() ?? 'standard'

  return {
    id: entry.id,
    orgId: entry.orgId,
    ledgerId,
    entryNumber: entry.entryNumber,
    entryDate: entry.entryDate,
    postDate: entry.postDate,
    entryType,
    memo: entry.memo,
    reference: entry.reference,
    status,
    /* v8 ignore next */
    lines: (entry.lines ?? []).map(line => {
      const amount = parseInt(line.amount?.original?.amount ?? '0', 10)
      return {
        id: line.id,
        lineNumber: line.lineNumber,
        accountId: line.accountId,
        debitAmount: line.isDebit ? amount : 0,
        creditAmount: line.isDebit ? 0 : amount,
        currency: line.amount?.original?.currency ?? 'INR',
        memo: line.memo ?? '',
      }
    }),
    createdAt: entry.audit?.createdAt ?? new Date().toISOString(),
    updatedAt: entry.audit?.updatedAt ?? new Date().toISOString(),
  }
}

/**
 * Handle org-switch logic: clear store if switching to a different org with data,
 * or simply update currentOrgId if the store is empty.
 */
function maybeResetForOrgSwitch(orgId: string): void {
  if (!localStore.initialized || localStore.currentOrgId === orgId) { return }

  const hasData = localStore.ledgers.size > 0 || localStore.accounts.size > 0
  if (hasData) {
    console.log(`[LocalDB] Switching from org ${localStore.currentOrgId} to ${orgId}, clearing data...`)
    localStore.initialized = false
    localStore.currentOrgId = null
    localStore.defaultLedgerId = null
    localStore.ledgers.clear()
    localStore.accounts.clear()
    localStore.journalEntries.clear()
    localStore.entities.clear()
    localStore.fiscalPeriods.clear()
  } else {
    console.log(`[LocalDB] Updating org ID from ${localStore.currentOrgId} to ${orgId} (no data to clear)`)
    localStore.currentOrgId = orgId
  }
}

/**
 * Fetch org from server and sync ledger + accounts + journal entries.
 * Returns true if data was successfully fetched from the server.
 */
async function syncOnlineData(orgId: string): Promise<boolean> {
  const orgResponse = await getOrganization(orgId)

  if (orgResponse.error) {
    if (orgResponse.error.code === '404') {
      console.log('[LocalDB] Organization not found on server (new setup in progress), using cached data')
    } else {
      console.warn('[LocalDB] Failed to fetch organization:', orgResponse.error)
    }
    return false
  }

  if (!orgResponse.data?.defaultLedgerId) {
    console.warn('[LocalDB] Organization does not have a default ledger ID')
    return false
  }

  const ledgerId = orgResponse.data.defaultLedgerId
  localStore.defaultLedgerId = ledgerId
  localStore.ledgers.clear()
  localStore.accounts.clear()
  localStore.fiscalPeriods.clear()

  const ledger: LocalLedger = {
    id: ledgerId, orgId, code: 'MAIN', name: 'Main Ledger',
    description: 'Main operating ledger', ledgerType: 'primary',
    currency: orgResponse.data.defaultCurrency,
    isActive: true, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  }
  localStore.ledgers.set(ledgerId, ledger)

  try { await syncAccountsFromServer(orgId, ledgerId) }
  catch (error) { console.error('[LocalDB] Failed to sync accounts:', error) }

  try { await syncJournalEntriesFromServer(orgId, ledgerId) }
  catch (error) { console.error('[LocalDB] Failed to sync journal entries:', error) }

  saveToLocalStorage()
  return true
}

/**
 * Initialize local database for web mode (non-Electron).
 * Tries online sync first; falls back to localStorage cache.
 */
async function initializeWebMode(orgId: string): Promise<void> {
  let hasOnlineDataFetched = false
  try {
    hasOnlineDataFetched = await syncOnlineData(orgId)
  } catch (error) {
    console.error('[LocalDB] Failed to connect to server:', error)
  }

  if (!hasOnlineDataFetched) {
    const hasLoaded = loadFromLocalStorage()
    if (!hasLoaded) {
      console.warn('[LocalDB] No cached data available')
      localStore.defaultLedgerId = DEFAULT_PRIMARY_LEDGER_ID
    }
  }
}

/**
 * Initialize local database with default data
 */
export async function initializeLocalDb(orgId: string): Promise<void> {
  maybeResetForOrgSwitch(orgId)

  // If already initialized for this organization, skip
  if (localStore.initialized && localStore.currentOrgId === orgId) { return }

  if (isElectron) {
    // Use Electron IPC for SQLite operations
    /* v8 ignore next */
    await getElectronWindow().electronAPI.db.initialize(orgId)
  } else {
    await initializeWebMode(orgId)
  }

  localStore.initialized = true
  localStore.currentOrgId = orgId
}

/**
 * Create only the primary ledger without seeding accounts
 * Currently unused but kept for future use
 */
/* v8 ignore next 25 */
// @ts-expect-error - Function kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for future use when offline ledger seeding is re-enabled
function seedPrimaryLedger(orgId: string): void {
  localStore.ledgers.clear()
  localStore.accounts.clear()

  const now = new Date().toISOString()
  const primaryLedger: LocalLedger = {
    id: DEFAULT_PRIMARY_LEDGER_ID, orgId, code: 'PRIMARY', name: 'Primary Ledger',
    description: 'Main operating ledger', ledgerType: 'primary', currency: 'INR',
    isActive: true, createdAt: now, updatedAt: now,
  }
  localStore.ledgers.set(DEFAULT_PRIMARY_LEDGER_ID, primaryLedger)
  localStore.defaultLedgerId = DEFAULT_PRIMARY_LEDGER_ID
} /* v8 ignore next */

function seedPrimaryLedgerEntry(orgId: string, now: string): void {
  const primaryLedger: LocalLedger = {
    id: DEFAULT_PRIMARY_LEDGER_ID, orgId, code: 'PRIMARY', name: 'Primary Ledger',
    description: 'Main operating ledger', ledgerType: 'primary', currency: 'INR',
    isActive: true, createdAt: now, updatedAt: now,
  }
  localStore.ledgers.set(DEFAULT_PRIMARY_LEDGER_ID, primaryLedger)
  localStore.defaultLedgerId = DEFAULT_PRIMARY_LEDGER_ID
}

function seedChartOfAccounts(orgId: string, now: string): void {
  const accountMap = new Map<string, string>()
  for (const defaultAccount of DEFAULT_CHART_OF_ACCOUNTS) {
    const id = crypto.randomUUID()
    accountMap.set(defaultAccount.code, id)
    const parentId = defaultAccount.parentCode ? accountMap.get(defaultAccount.parentCode) : undefined
    const account: LocalAccount = {
      id, orgId, ledgerId: DEFAULT_PRIMARY_LEDGER_ID,
      code: defaultAccount.code, name: defaultAccount.name,
      /* v8 ignore next */
      description: defaultAccount.description ?? '',
      accountType: defaultAccount.accountType,
      /* v8 ignore next */
      accountSubtype: defaultAccount.subtype ?? '',
      normalBalance: getNormalBalance(defaultAccount.accountType),
      ...(parentId !== undefined && { parentId }),
      path: defaultAccount.parentCode
        ? `${defaultAccount.parentCode}/${defaultAccount.code}`
        : defaultAccount.code,
      depth: defaultAccount.parentCode ? 1 : 0,
      isHeader: defaultAccount.isHeader ?? false,
      isSystem: false, isActive: true, createdAt: now, updatedAt: now,
    }
    localStore.accounts.set(id, account)
  }
}

/**
 * Seed default primary ledger and chart of accounts
 */
function seedDefaultData(orgId: string): void {
  localStore.ledgers.clear()
  localStore.accounts.clear()
  const now = new Date().toISOString()
  seedPrimaryLedgerEntry(orgId, now)
  seedChartOfAccounts(orgId, now)
}

function clearLocalStorageKeys(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('accounting_ledgers')
    localStorage.removeItem('accounting_accounts')
    localStorage.removeItem('accounting_journal_entries')
    localStorage.removeItem('accounting_entities')
    localStorage.removeItem('accounting_fiscal_periods')
    localStorage.removeItem('accounting_sequences')
    localStorage.removeItem('accounting_default_ledger_id')
    localStorage.removeItem('elaccounts_local_db')
  }
}

/**
 * Clear all local data and re-seed with defaults
 * This is useful when the chart of accounts structure has changed
 */
export async function clearAndReseedLocalDb(orgId: string): Promise<void> {
  if (isElectron) {
    // Use Electron IPC for SQLite operations
    /* v8 ignore next */
    await getElectronWindow().electronAPI.db.clearAndReseed(orgId)
    return
  }

  localStore.ledgers.clear()
  localStore.accounts.clear()
  localStore.journalEntries.clear()
  localStore.entities.clear()
  localStore.fiscalPeriods.clear()
  localStore.sequences.clear()
  localStore.initialized = false
  localStore.defaultLedgerId = null

  clearLocalStorageKeys()
  seedDefaultData(orgId)
  saveToLocalStorage()
  localStore.initialized = true
  localStore.currentOrgId = orgId
}

/**
 * Check if local database is initialized
 */
export function isLocalDbInitialized(): boolean {
  return localStore.initialized
}

/**
 * Get the default ledger ID
 */
export function getDefaultLedgerId(): string | null {
  return localStore.defaultLedgerId
}
